import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { financeApi, AllProjectsFinanceSummaryRow } from '../../api/finance';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';

function money(n: number) {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * The real cross-project rollup - every project with a budget for
 * the selected fiscal year, genuinely computed via the same
 * per-line logic as each project's own Finance tab, then summed to
 * a total per project. Distinct from that per-project tab: this is
 * the "everything at a glance" view.
 */
export function ProjectsFinanceSummaryPage() {
  const fiscalYearsQuery = useQuery({ queryKey: ['finance-fiscal-years'], queryFn: () => financeApi.listFiscalYears() });
  const [fiscalYearId, setFiscalYearId] = useState<number | null>(null);

  const summaryQuery = useQuery({
    queryKey: ['all-projects-finance-summary', fiscalYearId],
    queryFn: () => financeApi.getAllProjectsFinanceSummary(fiscalYearId!),
    enabled: !!fiscalYearId,
  });

  const rows = summaryQuery.data?.data.rows ?? [];
  const grandTotal = rows.reduce(
    (acc, r) => ({ budgeted: acc.budgeted + r.budgeted, actual: acc.actual + r.actual, committed: acc.committed + r.committed, available: acc.available + r.available, variance: acc.variance + r.variance }),
    { budgeted: 0, actual: 0, committed: 0, available: 0, variance: 0 }
  );

  return (
    <div className="page">
      <PageHeader title="Projects Finance Summary" actions={fiscalYearId && <a href={financeApi.allProjectsFinancePdfUrl(fiscalYearId)} target="_blank" rel="noreferrer">Download PDF</a>} />
      <p className="muted">Every project with a budget for the selected fiscal year, at a glance. For one project's own detail, see that project's own Finance tab.</p>

      <section className="card">
        <label>
          Fiscal Year
          <select onChange={(e) => setFiscalYearId(Number(e.target.value) || null)}>
            <option value="">- select -</option>
            {fiscalYearsQuery.data?.data.map((fy) => <option key={fy.id} value={fy.id}>{fy.year_label}</option>)}
          </select>
        </label>
      </section>

      {fiscalYearId && (
        <section className="card">
          <DataTable<AllProjectsFinanceSummaryRow>
            rows={rows}
            rowKey={(r) => r.projectId}
            emptyMessage="No project has a budget for this fiscal year yet."
            columns={[
              { header: 'Reference', render: (r) => <Link to={`/projects/${r.projectId}/finance`}>{r.projectReference}</Link> },
              { header: 'Project', render: (r) => r.projectName },
              { header: 'Budget', render: (r) => money(r.budgeted) },
              { header: 'Actual', render: (r) => money(r.actual) },
              { header: 'Committed', render: (r) => money(r.committed) },
              { header: 'Available', render: (r) => money(r.available) },
              { header: 'Variance', render: (r) => <span style={{ color: r.variance < 0 ? 'red' : undefined }}>{money(r.variance)}</span> },
            ]}
          />
          {rows.length > 0 && (
            <p style={{ marginTop: '1em' }}>
              <strong>Grand Total: Budget {money(grandTotal.budgeted)} · Actual {money(grandTotal.actual)} · Committed {money(grandTotal.committed)} · Available {money(grandTotal.available)} · Variance {money(grandTotal.variance)}</strong>
            </p>
          )}
        </section>
      )}
    </div>
  );
}
